import { useEffect, useState } from "react";
import { getAdSlot } from "@/lib/api";

/**
 * Non-intrusive ad slot (item 12).
 *
 * Reads HTML from `/api/ads/{position}` — empty by default so the slot
 * silently disappears until you (admin) configure it via the admin panel.
 *
 * Layout: wrapped in a subtle "Sponsored" caption + thin border so it always
 * blends visually with the page and never feels like a popup. You paste HTML
 * from any provider (Google AdSense, Carbon, BuySellAds, Ethical Ads, etc.).
 *
 * Available positions:
 *   - home-below-hero
 *   - home-footer
 *   - dashboard-top
 *   - dashboard-sidebar
 *   - pricing-bottom
 *
 * Usage:  <AdSlot position="home-below-hero" />
 */
export default function AdSlot({ position, className = "" }) {
  const [html, setHtml] = useState("");
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let mounted = true;
    getAdSlot(position)
      .then((d) => {
        if (!mounted) return;
        setHtml(d.html || "");
        setEnabled(!!d.enabled && !!(d.html || "").trim());
      })
      .catch(() => {
        if (mounted) setEnabled(false);
      });
    return () => { mounted = false; };
  }, [position]);

  if (!enabled) return null;

  return (
    <aside
      data-testid={`ad-slot-${position}`}
      className={`relative my-6 px-4 py-3 border border-zinc-200 bg-zinc-50/40 rounded-sm ${className}`}
      aria-label="Sponsored content"
    >
      <div className="absolute top-1 right-2 text-[8px] font-mono uppercase tracking-widest text-zinc-400 select-none">
        Sponsored
      </div>
      <div
        className="mt-1 [&_img]:max-w-full [&_iframe]:max-w-full text-sm"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </aside>
  );
}
